import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/Login.css';

const Login = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({
    organization: '',
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDbChoice, setShowDbChoice] = useState(false);
  const [dbChoice, setDbChoice] = useState('staging');
  const [dbConfigLabel, setDbConfigLabel] = useState(null);

  // Staging only: GET /api/db/choice exists on staging; production returns 404
  useEffect(() => {
    axios.get('/api/db/choice')
      .then((res) => {
        if (res.data && typeof res.data.db_choice === 'string') {
          setShowDbChoice(true);
          setDbChoice(res.data.db_choice === 'production' ? 'production' : 'staging');
        }
      })
      .catch(() => {});
  }, []);

  // Optional: fetch current DB config for label (host/database name)
  useEffect(() => {
    if (!showDbChoice) return;
    axios.get('/api/db/config')
      .then((res) => {
        if (res.data?.database) setDbConfigLabel(res.data.database);
      })
      .catch(() => {});
  }, [showDbChoice, dbChoice]);

  // Pre-fill org and username. Priority:
  //   1. Hostname subdomain when URL is <org>.dachido.com
  //   2. Last-session values from localStorage
  useEffect(() => {
    let orgFromHost = '';
    try {
      const host = (window.location.hostname || '').toLowerCase();
      // Match exactly <label>.dachido.com (single subdomain, not nested like foo.bar.dachido.com)
      const m = host.match(/^([a-z0-9][a-z0-9-]*)\.dachido\.com$/);
      if (m && m[1] !== 'www') orgFromHost = m[1];
    } catch (_) {}

    let orgFromStorage = '';
    let userFromStorage = '';
    try {
      const stored = localStorage.getItem('fieldforce_auth');
      if (stored) {
        const { organization, username } = JSON.parse(stored);
        orgFromStorage = organization || '';
        userFromStorage = username || '';
      }
    } catch (_) {}

    const nextOrg = orgFromHost || orgFromStorage;
    if (nextOrg || userFromStorage) {
      setCredentials(prev => ({
        ...prev,
        organization: nextOrg || prev.organization,
        username: userFromStorage || prev.username,
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    let org = credentials.organization?.trim() || '';
    if (!org) {
      setError('Organization is required.');
      setIsLoading(false);
      return;
    }
    const pwd = (credentials.password || '').trim();
    if (pwd.length > 0 && pwd.length <= 6) {
      setError('Password must be longer than 6 characters.');
      setIsLoading(false);
      return;
    }

    try {
      if (showDbChoice) {
        await axios.post('/api/db/choice', { db_choice: dbChoice });
      }
      const response = await axios.post('/api/auth/login', {
        organization: org,
        username: credentials.username.trim(),
        password: credentials.password
      });

      if (response.data.success) {
        // Successful login - store user info
        const userData = {
          organization: response.data.user.organization,
          username: response.data.user.username,
          role: response.data.user.role,
          loginTime: new Date().toISOString(),
          is_staging: showDbChoice,   // true if staging env (DB choice UI exists), false if production
          requested_db: showDbChoice ? dbChoice : undefined,
        };
        
        localStorage.setItem('fieldforce_auth', JSON.stringify(userData));
        onLogin(userData);
      } else {
        setError('Invalid credentials. Please check your organization, username, and password.');
        setIsLoading(false);
      }
    } catch (err) {
      // Handle API errors
      const errorMessage = err.response?.data?.error || 'Authentication failed. Please try again.';
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <div className="login-logo">FI</div>
          <h1>Field Intelligence Platform</h1>
          <p className="login-subtitle">Secure Access Portal</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {showDbChoice && (
            <div className="form-group">
              <label htmlFor="db_choice">Database</label>
              <select
                id="db_choice"
                value={dbChoice}
                onChange={(e) => setDbChoice(e.target.value)}
                disabled={isLoading}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
              >
                <option value="staging">Staging Database</option>
                <option value="production">Production Database</option>
              </select>
              {dbConfigLabel && <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>Current config: {dbConfigLabel}</small>}
              <small style={{ color: '#666', display: 'block', marginTop: '2px' }}>Choose which database to use for this session.</small>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="organization">Organization</label>
            <input
              type="text"
              id="organization"
              name="organization"
              value={credentials.organization}
              onChange={handleChange}
              placeholder="e.g. DRROOF"
              required
              autoComplete="organization"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={credentials.username}
              onChange={handleChange}
              placeholder="Enter username"
              required
              autoComplete="username"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={credentials.password}
              onChange={handleChange}
              placeholder="Enter password"
              required
              autoComplete="current-password"
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠</span>
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? 'Authenticating...' : 'Sign In'}
          </button>
        </form>

        <div className="login-footer">
          <p>© 2026 Field Intelligence Platform</p>
          <p className="security-notice">🔒 Secure Connection</p>
        </div>
      </div>
    </div>
  );
};

export default Login;





